import { DEMO_WINDOW, type DemoDay, shortDatePt, weekdayPt } from '@transp/shared'
import { useEffect, useMemo, useState } from 'react'
import { type Bus, busesAt, convoysAt } from './map/buses'
import { cssColor, lineColor, MapView, PALETTES } from './map/MapView'
import { Timeline } from './map/Timeline'
import { useDays } from './map/useDays'
import { usePlayback } from './map/usePlayback'
import { type TripsQuery, useTrips } from './map/useTrips'
import { type Theme, type ThemeChoice, useTheme } from './theme'

const LINES = [...DEMO_WINDOW.lines]

/** Rastro atras do veiculo, em segundos de tempo simulado. */
const TRAIL_S = 240

/** Diagnostico de maquina (latencia de consulta) so aparece com ?debug na URL. */
const DEBUG =
	typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug')

const num = new Intl.NumberFormat('pt-BR')

export default function App() {
	const { theme, choice, setChoice } = useTheme()
	const [day, setDay] = useState<string>(DEMO_WINDOW.day)

	const daysState = useDays({ from: DEMO_WINDOW.from, to: DEMO_WINDOW.to, lines: LINES })
	const days = daysState.status === 'ready' ? daysState.data.days : []

	// O dia padrao pode nao existir na lista - a base e reprocessada, e um dia pode sair.
	// Cair no mais recente e melhor que pedir uma janela vazia e mostrar tela em branco.
	useEffect(() => {
		if (days.length === 0) return
		if (days.some((d) => d.day === day)) return
		setDay(days[days.length - 1].day)
	}, [days, day])

	const query: TripsQuery = useMemo(
		() => ({ day, from: DEMO_WINDOW.from, to: DEMO_WINDOW.to, lines: LINES }),
		[day],
	)
	const state = useTrips(query)
	const durationS = state.status === 'ready' ? state.data.window.durationS : 0
	const { time, playing, speed, setSpeed, toggle, scrub } = usePlayback(durationS)
	const [selected, setSelected] = useState<number | null>(null)

	const trips = state.status === 'ready' ? state.data.trips : []
	// Contado pela MESMA regra do mapa: por viagem, nao por segmento. Contar segmentos
	// contaria duas vezes o onibus cuja linha esta partida por salto de GPS.
	const buses = useMemo(() => busesAt(trips, time), [trips, time])
	const moving = buses.filter((b) => b.stopped === false).length
	const stopped = buses.filter((b) => b.stopped === true).length
	// Onibus em comboio: alcancaram outro do MESMO sentido. E o fenomeno que a analise
	// mede, visivel no instante.
	const convoys = useMemo(() => convoysAt(buses).reduce((n, c) => n + c.members.length, 0), [buses])
	const chosen = selected === null ? null : (buses.find((b) => b.tripId === selected) ?? null)
	const chosenDay = days.find((d) => d.day === day) ?? null

	// Todas as ressalvas num lugar so: as do dado desenhado e as da lista de dias.
	const caveats = [
		...(state.status === 'ready' ? state.data.meta.caveats : []),
		...(daysState.status === 'ready' ? daysState.data.meta.caveats : []),
	]

	return (
		<div className="relative h-full w-full">
			{state.status === 'ready' && (
				<MapView
					trips={trips}
					buses={buses}
					currentTime={time}
					trailLength={TRAIL_S}
					selected={selected}
					onSelect={setSelected}
					theme={theme}
				/>
			)}

			<header className="pointer-events-none absolute left-4 top-4 z-10 w-[19rem] max-w-[calc(100vw-2rem)]">
				<div className="pointer-events-auto rounded-lg border border-rule bg-card/95 px-4 py-3.5 shadow-sm backdrop-blur">
					<div className="flex items-start justify-between gap-2">
						<div>
							<p className="font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-soft">
								Corredor
							</p>
							{/* O TITULO E A LEGENDA. Os dois numeros ja carregam a cor da sua linha, entao
							    a legenda embaixo so precisa explicar as FORMAS. Cor identifica a linha,
							    forma identifica o estado - e cada uma e explicada uma vez so. */}
							<h1 className="mt-0.5 font-display text-2xl font-semibold leading-none tracking-tight">
								<Line theme={theme} line="1758" />
								<span className="px-1.5 text-soft opacity-50">/</span>
								<Line theme={theme} line="1759" />
							</h1>
						</div>
						<ThemeButton choice={choice} onChange={setChoice} />
					</div>

					<DayPicker
						days={days}
						day={day}
						onPick={setDay}
						loading={daysState.status === 'loading'}
					/>
					<p className="mt-1.5 font-display text-xs uppercase tracking-wide text-soft">
						{DEMO_WINDOW.from}–{DEMO_WINDOW.to} · hora de São Paulo
					</p>

					{state.status === 'ready' && (
						<div className="mt-3 border-t border-rule pt-3">
							<p className="font-display text-xs uppercase tracking-wide text-soft">
								<Metric n={state.data.meta.trips} /> viagens
								<span className="px-1.5 opacity-40">·</span>
								<Metric n={state.data.meta.points} /> posições
								{DEBUG && (
									<>
										<span className="px-1.5 opacity-40">·</span>
										<Metric n={state.data.meta.queryMs} /> ms
									</>
								)}
							</p>
							{chosenDay && (
								<p className="mt-0.5 font-display text-xs uppercase tracking-wide text-soft opacity-70">
									{chosenDay.backfilled
										? `processado em lote, ${shortDatePt(chosenDay.processedOn)}`
										: `processado em ${shortDatePt(chosenDay.processedOn)}`}
								</p>
							)}
							{chosenDay && <DayMarks day={chosenDay} />}
						</div>
					)}

					{state.status === 'ready' && <StateLegend />}
					{state.status === 'loading' && (
						<p className="mt-3 font-display text-xs uppercase tracking-wide text-soft">
							Carregando
						</p>
					)}
					{state.status === 'error' && <Failure text={state.message} />}
					{daysState.status === 'error' && (
						<Failure text={`Lista de dias indisponível. ${daysState.message}`} />
					)}
				</div>

				{state.status === 'ready' && (
					<Selected
						bus={chosen}
						pending={selected !== null && chosen === null}
						onDrop={() => setSelected(null)}
						theme={theme}
					/>
				)}

				{caveats.length > 0 && (
					<details className="pointer-events-auto mt-2 rounded-lg border border-rule bg-card/95 px-4 py-2 text-xs shadow-sm backdrop-blur">
						<summary className="cursor-pointer font-display uppercase tracking-wide text-soft">
							Ressalvas do dado ({caveats.length})
						</summary>
						<ul className="mt-2 max-h-72 list-disc space-y-1.5 overflow-y-auto pl-4 leading-snug text-soft">
							{caveats.map((c) => (
								<li key={c}>{c}</li>
							))}
						</ul>
					</details>
				)}
			</header>

			{state.status === 'ready' && (
				<div className="absolute inset-x-4 bottom-4 z-10 mx-auto max-w-2xl">
					<Timeline
						from={DEMO_WINDOW.from}
						durationS={durationS}
						time={time}
						playing={playing}
						speed={speed}
						running={buses.length}
						convoys={convoys}
						moving={moving}
						stopped={stopped}
						onToggle={toggle}
						onScrub={scrub}
						onSpeed={setSpeed}
					/>
				</div>
			)}
		</div>
	)
}

/** Numero da linha, na cor com que ela e desenhada no mapa. */
function Line({ theme, line }: { theme: Theme; line: string }) {
	return (
		<span
			className="font-mono tabular-nums"
			style={{ color: cssColor(lineColor(PALETTES[theme], line)) }}
		>
			{line}
		</span>
	)
}

function Metric({ n }: { n: number }) {
	return <span className="font-mono text-[13px] tabular-nums text-ink">{num.format(n)}</span>
}

function Failure({ text }: { text: string }) {
	return <p className="mt-3 text-xs leading-snug text-mark">{text}</p>
}

/**
 * Seletor de dia.
 *
 * POR QUE ELE EXISTE: uma tela travada num dia so nao tem resposta para "por que voce
 * escolheu justo esse dia?". Com a lista aberta, quem pergunta responde sozinho - troca o
 * dia e olha. Se todos os dias uteis mostrarem o mesmo padrao, a pergunta morre; se algum
 * nao mostrar, precisamos saber disso antes de a Sambaiba saber.
 *
 * Os dias vem AGRUPADOS pela fronteira em que o detector da fonte parou de rejeitar
 * registro. Nao e arrumacao: o que a tela desenha segue a mesma regra nos dois lados, mas
 * o que a FONTE afirma sobre o dia nao segue, e quem compara precisa saber.
 */
function DayPicker({
	days,
	day,
	onPick,
	loading,
}: {
	days: DemoDay[]
	day: string
	onPick: (d: string) => void
	loading: boolean
}) {
	const validados = days.filter((d) => d.sourceValidates)
	const naoValidados = days.filter((d) => !d.sourceValidates)

	return (
		<label className="mt-3 block">
			<span className="sr-only">Dia</span>
			<select
				value={day}
				disabled={days.length === 0}
				onChange={(e) => onPick(e.target.value)}
				className="w-full rounded border border-rule bg-paper px-2 py-1.5 font-display text-sm font-medium disabled:opacity-60"
			>
				{days.length === 0 && (
					<option value={day}>{loading ? 'carregando dias' : shortDatePt(day)}</option>
				)}
				{validados.length > 0 && (
					<optgroup label="Fonte ainda validava as viagens (até 17/08)">
						{validados.map((d) => (
							<option key={d.day} value={d.day}>
								{optionLabel(d)}
							</option>
						))}
					</optgroup>
				)}
				{naoValidados.length > 0 && (
					<optgroup label="Fonte parou de validar (a partir de 18/08)">
						{naoValidados.map((d) => (
							<option key={d.day} value={d.day}>
								{optionLabel(d)}
							</option>
						))}
					</optgroup>
				)}
			</select>
		</label>
	)
}

/**
 * SEM contagem de viagens aqui, de proposito.
 *
 * A lista conta viagens que cruzam a janela segundo `jornadas_v2`; o painel conta as que o
 * mapa consegue desenhar, que sao um pouco menos (viagem com menos de tres pontos apos a
 * limpeza de espicula nao vira trajetoria). Medido: identicos em 11/08, quatro de
 * diferenca em 18/08. Sao duas medidas honestas de coisas diferentes, e mostrar as duas na
 * mesma tela chamando as duas de "viagens" e pior que mostrar uma. Fica a do painel, que e
 * a do que esta no mapa.
 */
function optionLabel(d: DemoDay): string {
	const parcial = isPartial(d) ? ` · parcial desde ${d.firstAt}` : ''
	return `${weekdayPt(d.day)}, ${shortDatePt(d.day)}${parcial}`
}

/** Dia cuja coleta nao cobre a madrugada inteira. Fato medido, vindo de `jornadas_v2`. */
function isPartial(d: DemoDay): boolean {
	return d.firstAt > '01:00'
}

/**
 * O que este dia tem de DIFERENTE dos outros. So isso.
 *
 * Antes havia uma etiqueta de cadencia e uma de regra de viagem em todos os dias. Fato
 * que vale para os treze dias da lista nao e informacao sobre o dia escolhido - e ruido
 * que aparece sempre e por isso deixa de ser lido. Os dois foram para as ressalvas, onde
 * ficam disponiveis sem ocupar a face do painel.
 *
 * O que sobrou aparece SO quando se aplica, e por isso uma marca aqui quer dizer alguma
 * coisa: este dia nao e como os outros. Em 11/08 nao aparece nenhuma; em 20/08, duas.
 */
function DayMarks({ day }: { day: DemoDay }) {
	const marks: { text: string; title: string }[] = []
	if (!day.sourceValidates) {
		marks.push({
			text: 'fonte parou de validar as viagens',
			title:
				'A partir de 18/08 o detector da fonte parou de rejeitar registro: 100,0% passaram ' +
				'a ser marcados válidos, contra 75,8% a 82,5% até 17/08. O total do dia não mudou ' +
				'(~375 a 400 dos dois lados), só a composição — entre os marcados completos, os que ' +
				'andaram menos de 500 m foram de 1,0-1,8% para 5,7%, 7,1% e 10,6%. O que esta tela ' +
				'desenha não depende dessa marca, mas o que a fonte afirma sobre o dia, sim.',
		})
	}
	if (isPartial(day)) {
		marks.push({
			text: `dia parcial, começa ${day.firstAt}`,
			title:
				'A coleta de 60 s deste dia começa às 05:43. Cobre a janela da manhã inteira, mas o ' +
				'dia não está completo.',
		})
	}
	if (day.keepsLoneVehicles) {
		marks.push({
			text: 'ingestão guarda linha com 1 ônibus',
			title:
				'A partir de 19/08 às 21h a ingestão passou a guardar os minutos em que uma linha ' +
				'tinha um ônibus só; antes eram descartados. Medido: na janela de 06h às 09h o ' +
				'corredor nunca tem um ônibus só, então isso não muda nada aqui. Num dia inteiro, muda.',
		})
	}
	if (marks.length === 0) return null
	return (
		<ul className="mt-2 space-y-1">
			{marks.map((m) => (
				<li
					key={m.text}
					title={m.title}
					className="flex cursor-help gap-1.5 font-display text-xs uppercase leading-tight tracking-wide text-soft"
				>
					<span aria-hidden className="text-mark">
						▲
					</span>
					{m.text}
				</li>
			))}
		</ul>
	)
}

/**
 * A legenda explica FORMA, nunca cor.
 *
 * A cor ja foi explicada pelo titulo, onde 1758 e 1759 aparecem cada um no seu tom. Repetir
 * dois quadradinhos coloridos aqui embaixo seria dizer a mesma coisa duas vezes e gastar
 * metade da legenda com isso. Por isso os simbolos aqui sao de TINTA: o que eles carregam
 * e o contorno e a opacidade, que e o que muda entre andando, parado e sem medicao.
 */
function StateLegend() {
	return (
		<ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-rule pt-3 font-display text-xs uppercase tracking-wide text-soft">
			<li className="flex items-center gap-1.5">
				<span className="h-2.5 w-2.5 shrink-0 rounded-full border border-ink" />
				parado
			</li>
			<li className="flex items-center gap-1.5">
				<span className="h-2.5 w-2.5 shrink-0 rounded-full bg-ink opacity-35" />
				posição mantida
			</li>
		</ul>
	)
}

const THEME_CYCLE: Record<ThemeChoice, ThemeChoice> = {
	sistema: 'claro',
	claro: 'escuro',
	escuro: 'sistema',
}
const THEME_ICON: Record<ThemeChoice, string> = { sistema: '◐', claro: '☀', escuro: '☾' }

function ThemeButton({
	choice,
	onChange,
}: {
	choice: ThemeChoice
	onChange: (c: ThemeChoice) => void
}) {
	const next = THEME_CYCLE[choice]
	return (
		<button
			type="button"
			onClick={() => onChange(next)}
			title={`Tema: ${choice}. Clique para ${next}.`}
			aria-label={`Tema: ${choice}. Trocar para ${next}.`}
			className="grid h-7 w-7 shrink-0 place-items-center rounded border border-rule text-xs text-soft hover:text-ink"
		>
			<span aria-hidden>{THEME_ICON[choice]}</span>
		</button>
	)
}

/**
 * Painel do carro escolhido.
 *
 * `pending` cobre o caso em que a viagem selecionada terminou enquanto o relogio andava: a
 * selecao continua valida, mas nao ha o que mostrar naquele instante. Some silenciosa seria
 * pior - quem esta seguindo o carro precisa saber que ele acabou a viagem.
 */
function Selected({
	bus,
	pending,
	onDrop,
	theme,
}: {
	bus: Bus | null
	pending: boolean
	onDrop: () => void
	theme: Theme
}) {
	if (!bus && !pending) return null
	return (
		<div className="pointer-events-auto mt-2 rounded-lg border border-rule bg-card/95 px-4 py-3 shadow-sm backdrop-blur">
			<div className="flex items-baseline justify-between gap-3">
				<p className="font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-soft">
					Veículo
				</p>
				<button
					type="button"
					onClick={onDrop}
					className="font-display text-xs uppercase tracking-wide text-soft hover:text-ink"
				>
					soltar
				</button>
			</div>
			{bus ? (
				<>
					<p className="mt-0.5 flex items-baseline gap-2 font-mono text-xl leading-none tabular-nums">
						{bus.vehicle}
						<span
							className="text-sm"
							style={{ color: cssColor(lineColor(PALETTES[theme], bus.line)) }}
						>
							{bus.line}
						</span>
					</p>
					<p className="mt-2 font-display text-xs uppercase tracking-wide text-soft">
						{bus.direction === 1 ? 'ida' : 'volta'}
						<span className="px-1.5 opacity-40">·</span>
						{bus.kmh === null ? (
							'sem velocidade'
						) : (
							<>
								<span className="font-mono text-[13px] tabular-nums text-ink">
									{bus.kmh.toFixed(0)}
								</span>{' '}
								km/h
							</>
						)}
					</p>
				</>
			) : (
				<p className="mt-1 font-display text-xs uppercase tracking-wide text-soft">
					Fora de viagem neste instante
				</p>
			)}
			<p className="mt-2 text-xs leading-snug text-soft">
				{bus?.held
					? 'Posição mantida: não há medição neste instante, o último ponto conhecido está sendo repetido.'
					: 'Velocidade estimada sobre 90 s. Com cadência de 60 s, é média do trecho, não instantânea.'}
			</p>
		</div>
	)
}
